import { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { QRScanner } from '../capture/components/QRScanner';
import { ThreePanelLayout } from './components/ThreePanelLayout';
import { LogoutModal } from '../../components/LogoutModal';
import { ToastProvider } from '../../components/ToastContainer';
import { listHostPhotos, downloadHostPhoto } from '../../lib/bridge/photo-host-client';
import { getEffectiveApiBaseUrl } from '../../lib/config/terminal-urls';
import { getCaptureConfig } from '../../lib/config/capture';
import { useOutletAuth } from '../../lib/auth/OutletAuthProvider';
import { useBranding } from '../../lib/branding';
import type { Transaction as APITransaction, Product as APIProduct } from '@photobooth/types';
import type { EditSessionTransaction, EditSessionProduct, EditSessionPhoto } from './types/edit-session';

const apiClient = createPhotoBoothAPI({
  baseUrl: getEffectiveApiBaseUrl(),
});

interface EditSession {
  transaction: EditSessionTransaction;
  product: EditSessionProduct;
  photos: EditSessionPhoto[];
  outletId: string;
}

type ViewState = 'scan' | 'loading' | 'edit' | 'success' | 'error';

export default function EditTerminal() {
  const [viewState, setViewState] = useState<ViewState>('scan');
  const [session, setSession] = useState<EditSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
  const [manualOrderId, setManualOrderId] = useState('');
  /** Edit countdown duration in seconds (from capture settings API: GET /api/config/capture). */
  const [editCountdownDuration, setEditCountdownDuration] = useState<number | null>(null);
  /** Remaining seconds for edit countdown. */
  const [editCountdownRemaining, setEditCountdownRemaining] = useState<number | null>(null);
  const editStartTimeRef = useRef<number | null>(null);
  /** Success screen countdown (seconds until returning to scan). */
  const [successCountdown, setSuccessCountdown] = useState(5);

  const navigate = useNavigate();
  const { outlet, authState, clearAuthentication } = useOutletAuth();
  const { branding } = useBranding();

  // Sync auth token to this page's API client so requests (e.g. updateEditStatus, getById) include Authorization
  useEffect(() => {
    try {
      const token =
        localStorage.getItem('photobooth_device_token') ||
        localStorage.getItem('photobooth_auth_token');
      if (token) {
        apiClient.setAuthToken(token);
      }
    } catch {
      // Non-critical if storage is unavailable
    }
  }, [authState]);

  const handleQRScan = useCallback(async (qrData: string) => {
    setViewState('loading');
    setError(null);

    try {
      const transactionId = qrData.trim();

      // Load transaction from API only (no IndexedDB)
      const txnResponse = await apiClient.transactions.getById(transactionId);
      if (!txnResponse.success || !txnResponse.data) {
        throw new Error('Transaction not found. Please check your order ID or QR code.');
      }
      const apiTxn = txnResponse.data as APITransaction;

      if (apiTxn.captureStatus !== 'completed') {
        throw new Error('Photos have not been captured yet. Please complete the capture session first.');
      }
      if (apiTxn.editStatus === 'completed') {
        throw new Error('This session has already been edited and printed.');
      }

      const transaction: EditSessionTransaction = {
        transactionId: apiTxn.transactionId,
        customerName: apiTxn.customer.name,
        productId: apiTxn.productId,
        editStatus: 'in_progress',
      };

      // Load product from API only (no IndexedDB)
      const productResponse = await apiClient.products.getById(apiTxn.productId);
      if (!productResponse.success || !productResponse.data) {
        throw new Error('Product information not found.');
      }
      const apiProduct = productResponse.data as APIProduct;
      const product: EditSessionProduct = {
        productId: apiProduct.productId,
        name: apiProduct.name,
        shotCount: apiProduct.shotCount,
        printCount: apiProduct.printCount,
        paperType: apiProduct.paperType as 'collage_4x6' | 'strip',
      };

      // Load photos from Bridge only; keep in React state (no IndexedDB)
      const hostPhotos = await listHostPhotos(transactionId);
      if (hostPhotos.length === 0) {
        throw new Error(
          'No photos found for this session. Please complete capture and ensure the Bridge host is running.'
        );
      }

      const photos: EditSessionPhoto[] = [];
      for (const hostPhoto of hostPhotos) {
        const blob = await downloadHostPhoto(transactionId, hostPhoto.sequence);
        const id = `edit_${transactionId}_${hostPhoto.sequence}`;
        photos.push({
          id,
          photoId: id,
          imageBlob: blob,
          sequence: hostPhoto.sequence,
        });
      }

      if (photos.length !== product.shotCount) {
        console.warn(
          `Photo count mismatch: expected ${product.shotCount}, found ${photos.length}`
        );
      }

      try {
        await apiClient.transactions.updateEditStatus(transactionId, 'in_progress');
      } catch (apiErr) {
        console.warn('Failed to update edit status on API:', apiErr);
      }

      setSession({
        transaction,
        product,
        photos,
        outletId: authState?.outletId || outlet?.outletId || '',
      });
      setViewState('edit');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load session';
      setError(errorMessage);
      setViewState('error');
    }
  }, [authState?.outletId, outlet?.outletId]);

  const handleBackToScan = useCallback(() => {
    setSession(null);
    setError(null);
    setManualOrderId('');
    setViewState('scan');
  }, []);

  /** Called when user confirms and save succeeds; show success then countdown to scan. */
  const handleEditComplete = useCallback(() => {
    setViewState('success');
    setSuccessCountdown(5);
  }, []);

  const handleConfirmLeave = useCallback(() => {
    setShowBackConfirmModal(false);
    handleBackToScan();
    navigate('/');
  }, [navigate, handleBackToScan]);

  // Fetch edit countdown from capture settings (GET /api/config/capture) when entering edit view
  useEffect(() => {
    if (viewState !== 'edit' || !session) return;
    getCaptureConfig()
      .then((config) => {
        const seconds = config.editCountdownSeconds;
        setEditCountdownDuration(seconds > 0 ? seconds : 600);
      })
      .catch(() => {
        setEditCountdownDuration(600);
      });
  }, [viewState, session]);

  // Edit countdown timer: run when in edit view using duration from capture settings (only after config loaded)
  const DEFAULT_EDIT_COUNTDOWN_SECONDS = 600;
  useEffect(() => {
    if (viewState !== 'edit' || !session) {
      setEditCountdownRemaining(null);
      editStartTimeRef.current = null;
      return;
    }
    if (editCountdownDuration == null) {
      setEditCountdownRemaining(null);
      return; // wait for GET /api/config/capture to return
    }
    const totalSeconds = editCountdownDuration > 0 ? editCountdownDuration : DEFAULT_EDIT_COUNTDOWN_SECONDS;
    if (editStartTimeRef.current === null) {
      editStartTimeRef.current = Date.now();
    }
    const tick = () => {
      const elapsed = Math.floor((Date.now() - (editStartTimeRef.current ?? 0)) / 1000);
      const remaining = Math.max(0, totalSeconds - elapsed);
      setEditCountdownRemaining(remaining);
      if (remaining <= 0) {
        editStartTimeRef.current = null;
      }
    };
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [viewState, session, editCountdownDuration]);

  // Reset countdown when leaving edit view
  useEffect(() => {
    if (viewState !== 'edit') {
      editStartTimeRef.current = null;
      setEditCountdownDuration(null);
      setEditCountdownRemaining(null);
    }
  }, [viewState]);

  // Success screen: 5-second countdown then back to scan
  useEffect(() => {
    if (viewState !== 'success') return;
    if (successCountdown <= 0) {
      handleBackToScan();
      return;
    }
    const t = setTimeout(() => setSuccessCountdown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [viewState, successCountdown, handleBackToScan]);

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <ToastProvider>
      <DndProvider backend={HTML5Backend}>
        <div 
          className="min-h-screen p-8"
          style={{
            background: branding.colors.background || 'linear-gradient(to bottom right, rgb(254, 252, 232), rgb(255, 237, 213))',
          }}
        >
      <div className="w-full px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            {/* Outlet Logo */}
            {branding.logos.primary && (
              <img 
                src={branding.logos.primary} 
                alt={branding.businessName}
                className="h-12 object-contain"
              />
            )}
            <div>
              <h1 
                className="text-4xl font-bold"
                style={{
                  color: branding.colors.primary || '#facc15',
                  fontFamily: branding.fonts.heading,
                }}
              >
                Edit
              </h1>
              {outlet && (
                <p 
                  className="text-sm mt-1"
                  style={{ color: branding.colors.textMuted || '#6b7280' }}
                >
                  {outlet.name} - {outlet.location}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {viewState === 'edit' ? (
              <button
                type="button"
                onClick={() => setShowBackConfirmModal(true)}
                className="px-6 py-3 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all"
                style={{ color: branding.colors.text || '#374151' }}
              >
                ← Back
              </button>
            ) : (
              <Link
                to="/"
                className="px-6 py-3 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all"
                style={{ color: branding.colors.text || '#374151' }}
              >
                ← Back
              </Link>
            )}
            <button
              type="button"
              onClick={() => setShowLogoutModal(true)}
              className="px-6 py-3 rounded-2xl bg-red-500 text-white shadow-lg hover:bg-red-600 hover:shadow-xl transition-all font-semibold"
            >
              Logout
            </button>
          </div>
        </div>

        {/* QR Scan View */}
        {viewState === 'scan' && (
          <div className="bg-white rounded-3xl shadow-xl p-12">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">✨</div>
              <h2 
                className="text-3xl font-bold mb-2"
                style={{ 
                  color: branding.colors.primary || '#facc15',
                  fontFamily: branding.fonts.heading,
                }}
              >
                Scan Your QR Code
              </h2>
              <p 
                className="text-lg"
                style={{ color: branding.colors.textMuted || '#6b7280' }}
              >
                Scan the QR code from your receipt to edit your photos
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <QRScanner onScan={handleQRScan} />
            </div>

            <div className="max-w-md mx-auto mt-8 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-3 text-center">
                QR code not working? Enter order ID manually
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  autoFocus
                  value={manualOrderId}
                  onChange={(e) => setManualOrderId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && manualOrderId.trim()) {
                      e.preventDefault();
                      handleQRScan(manualOrderId.trim());
                    }
                  }}
                  placeholder="e.g. PB-20260209-5E1C60CA"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => {
                    const id = manualOrderId.trim();
                    if (id) handleQRScan(id);
                  }}
                  disabled={!manualOrderId.trim()}
                  className="px-6 py-3 rounded-xl text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{
                    backgroundColor: branding.colors.primary || '#facc15',
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading View */}
        {viewState === 'loading' && (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <div className="text-6xl mb-6 animate-bounce">📸</div>
            <h2 
              className="text-3xl font-bold mb-4"
              style={{ 
                color: branding.colors.primary || '#facc15',
                fontFamily: branding.fonts.heading,
              }}
            >
              Loading Your Photos...
            </h2>
            <div className="flex justify-center">
              <div 
                className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: `${branding.colors.primary || '#facc15'} transparent transparent transparent` }}
              />
            </div>
          </div>
        )}

        {/* Success View (after confirm) */}
        {viewState === 'success' && (
          <div className="bg-white rounded-3xl shadow-xl p-12">
            <div className="text-center">
              <div className="text-6xl mb-6">✅</div>
              <h2
                className="text-3xl font-bold mb-2"
                style={{
                  color: branding.colors.primary || '#22c55e',
                  fontFamily: branding.fonts.heading,
                }}
              >
                Success!
              </h2>
              <p
                className="text-lg mb-6"
                style={{ color: branding.colors.textMuted || '#6b7280' }}
              >
                Your edits have been saved. Returning to scan in{' '}
                <span className="font-bold tabular-nums" role="timer" aria-live="polite">
                  {successCountdown}
                </span>{' '}
                second{successCountdown !== 1 ? 's' : ''}...
              </p>
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full text-2xl font-bold text-white"
                style={{ backgroundColor: branding.colors.primary || '#22c55e' }}
              >
                {successCountdown}
              </div>
            </div>
          </div>
        )}

        {/* Error View */}
        {viewState === 'error' && (
          <div className="bg-white rounded-3xl shadow-xl p-12">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">❌</div>
              <h2 className="text-3xl font-bold mb-4 text-red-600">Oops!</h2>
              <p 
                className="text-lg mb-8"
                style={{ color: branding.colors.text || '#374151' }}
              >
                {error}
              </p>
              <button
                onClick={handleBackToScan}
                className="px-8 py-4 rounded-2xl text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                style={{
                  background: `linear-gradient(to right, ${branding.colors.primary || '#facc15'}, ${branding.colors.secondary || '#fb923c'})`,
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Edit View */}
        {viewState === 'edit' && session && (
          <div className="space-y-6">
            {/* Session Info */}
            <div className="bg-white rounded-3xl shadow-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 
                    className="text-2xl font-bold mb-2"
                    style={{ 
                      color: branding.colors.primary || '#facc15',
                      fontFamily: branding.fonts.heading,
                    }}
                  >
                    {session.product.name}
                  </h2>
                  <p style={{ color: branding.colors.textMuted || '#6b7280' }}>
                    {session.photos.length} photos • {session.product.printCount} prints
                  </p>
                </div>
                <div className="flex items-center gap-6">
                  {editCountdownRemaining !== null && (
                    <div
                      className="flex flex-col items-center px-4 py-2 rounded-xl border-2 font-semibold tabular-nums"
                      style={{
                        borderColor: branding.colors.primary || '#facc15',
                        color: branding.colors.primary || '#374151',
                      }}
                      role="timer"
                      aria-live="polite"
                      aria-label={`Time remaining: ${formatCountdown(editCountdownRemaining)}`}
                    >
                      <span className="text-xs uppercase tracking-wide" style={{ color: branding.colors.textMuted || '#6b7280' }}>
                        Time left
                      </span>
                      <span className="text-2xl">{formatCountdown(editCountdownRemaining)}</span>
                    </div>
                  )}
                  <div className="text-right" style={{ color: branding.colors.textMuted || '#6b7280' }}>
                    <p className="font-medium" style={{ color: branding.colors.text || '#374151' }}>
                      {session.transaction.customerName}
                    </p>
                    <p className="text-sm mt-1">
                      Order ID: {session.transaction.transactionId}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Three-Panel Layout */}
            <ThreePanelLayout
              photos={session.photos}
              product={session.product}
              transaction={session.transaction}
              outletId={session.outletId}
              onComplete={handleEditComplete}
              onCancel={handleBackToScan}
            />
          </div>
        )}
      </div>

      {showLogoutModal && authState?.outletId && (
        <LogoutModal
          outletId={authState.outletId}
          terminalName="Edit"
          onClose={() => setShowLogoutModal(false)}
          onLogout={async () => {
            await clearAuthentication();
            navigate('/auth');
          }}
        />
      )}

      {showBackConfirmModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="back-confirm-title"
          aria-describedby="back-confirm-description"
        >
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
            <h3 id="back-confirm-title" className="text-2xl font-bold text-gray-800 mb-2">
              Leave without saving?
            </h3>
            <p id="back-confirm-description" className="text-gray-600 mb-6">
              Your edits will not be saved. Are you sure you want to go back?
            </p>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setShowBackConfirmModal(false)}
                className="flex-1 px-6 py-3 rounded-xl bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 focus:outline-none focus:ring-4 focus:ring-gray-400"
                aria-label="Stay"
              >
                Stay
              </button>
              <button
                type="button"
                onClick={handleConfirmLeave}
                className="flex-1 px-6 py-3 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 focus:outline-none focus:ring-4 focus:ring-red-400"
                aria-label="Leave"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </DndProvider>
    </ToastProvider>
  );
}
